import React, { useEffect, useContext, useMemo, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageContext } from "./components/LanguageContext";
import MainLayout from "./layouts/MainLayout";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load components
const Home = lazy(() => import("./pages/en/home"));
const About = lazy(() => import("./pages/en/about"));
const Projects = lazy(() => import("./pages/en/projects"));
const Services = lazy(() => import("./pages/en/services"));
const Products = lazy(() => import("./pages/en/products"));
const Training = lazy(() => import("./pages/en/training"));
const News = lazy(() => import("./pages/en/news"));

const HomeAr = lazy(() => import("./pages/ar/home"));
const AboutAr = lazy(() => import("./pages/ar/about"));
const ProjectsAr = lazy(() => import("./pages/ar/projects"));
const ServicesAr = lazy(() => import("./pages/ar/services"));
const ProductsAr = lazy(() => import("./pages/ar/products"));
const TrainingAr = lazy(() => import("./pages/ar/training"));
const NewsAr = lazy(() => import("./pages/ar/news"));

function App() {
  const { language } = useContext(LanguageContext);
  const location = useLocation();

  useEffect(() => {
    const rootBody = document.getElementById("rootBody");
    if (rootBody) {
      const dir = language === "ar" ? "rtl" : "ltr";
      rootBody.setAttribute("dir", dir);
      rootBody.setAttribute("lang", language);
      rootBody.className = dir;
    }
  }, [language]);

  // Memoize routes configuration
  const routes = useMemo(() => [
    { path: "home", en: Home, ar: HomeAr },
    { path: "about", en: About, ar: AboutAr },
    { path: "projects", en: Projects, ar: ProjectsAr },
    { path: "services", en: Services, ar: ServicesAr },
    { path: "products", en: Products, ar: ProductsAr },
    { path: "training", en: Training, ar: TrainingAr },
    { path: "news", en: News, ar: NewsAr },
  ], []);

  return (
    <ErrorBoundary>
      <Routes>
        {/* Redirect from root to the current language's home */}
        <Route index element={<Navigate to={`/${language}/home`} replace />} />
        
        {/* English routes */}
        <Route path="en" element={<MainLayout />}>
          {routes.map(({ path, en: Component }) => (
            <Route
              key={`en-${path}`}
              path={path}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Component />
                </Suspense>
              }
            />
          ))}
          <Route index element={<Navigate to="home" replace />} />
        </Route>

        {/* Arabic routes */}
        <Route path="ar" element={<MainLayout />}>
          {routes.map(({ path, ar: Component }) => (
            <Route
              key={`ar-${path}`}
              path={path}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Component />
                </Suspense>
              }
            />
          ))}
          <Route index element={<Navigate to="home" replace />} />
        </Route>

        {/* Catch all redirect to the current language's home */}
        <Route path="*" element={<Navigate to={`/${language}/home`} replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default React.memo(App);